// eslint-disable-next-line import/no-extraneous-dependencies
import chalk from 'chalk';

import { isDirectConsolePrinter, parseLogLevel } from '../config';

import type { LogLevel, LoggerPlugin, ConsolePatch } from '../types';

export const CONSOLE_MIN_LEVEL: LogLevel = parseLogLevel(
  process.env.NEXT_PUBLIC_LOG_LEVEL_CONSOLE,
  'info',
);

/* Use chalk only in Node to avoid bundling it for browser */
const colorize = typeof window === 'undefined' ? chalk : undefined!;

type PatchedGlobal = typeof globalThis & {
  [key: symbol]: ConsolePatch | undefined;
};

/** Resolve the original (pre-patch) console methods at call time, not import time */
const getOrig = (): ConsolePatch => {
  const patchedGlobal = globalThis as unknown as PatchedGlobal;
  const saved = patchedGlobal[Symbol.for('__logger_console_patch__')] as
    | ConsolePatch
    | undefined;
  return (
    saved ?? {
      warn: console.warn,
      error: console.error,
      info: console.info,
      debug: console.debug ?? console.log,
    }
  );
};

const flattenContext = (ctx: Record<string, unknown>) =>
  Object.entries(ctx || {})
    .map(([k, v]) => `${k}=${JSON.stringify(v)}`)
    .join(' ');

export const ConsolePlugin: LoggerPlugin = {
  name: 'console',
  minLevel: CONSOLE_MIN_LEVEL,

  handle(entry) {
    /* Browser: suppress output in production — logs are forwarded to other plugins */
    if (typeof window !== 'undefined' && !isDirectConsolePrinter()) {
      return;
    }

    const { level, message, context = {}, timestamp } = entry;
    const time = timestamp.toISOString();

    const levelTxt = {
      debug: 'DEBUG',
      info: 'INFO ',
      warn: 'WARN ',
      error: 'ERROR',
    }[level];

    const orig = getOrig();

    /* Choose printer & colour (Node only) */
    const printer =
      level === 'error'
        ? orig.error
        : level === 'warn'
          ? orig.warn
          : level === 'info'
            ? orig.info
            : orig.debug;

    const coloured = (txt: string) =>
      typeof window !== 'undefined'
        ? txt
        : level === 'error'
          ? colorize.red(txt)
          : level === 'warn'
            ? colorize.yellow(txt)
            : level === 'info'
              ? colorize.cyan(txt)
              : colorize.gray(txt);

    const line =
      `[${time}] ${coloured(levelTxt)}  ${message}` +
      (Object.keys(context).length ? `\n  ${flattenContext(context)}` : '');

    printer(line + '\n');
  },
};
